import React, { useState } from "react";
import { Outlet } from "react-router-dom";
import { useMe } from "@/lib/auth";
import AuthGate from "@/components/dashboard/AuthGate";
import Shell from "@/components/dashboard/Shell";
import UnlockModal from "@/components/dashboard/UnlockModal";
import LeadGateModal from "@/components/dashboard/LeadGateModal";
import DashboardTour from "@/components/dashboard/DashboardTour";
import SEO from "@/components/SEO";
import { hydrateFromServer } from "@/lib/storage";
import { trackEvent } from "@/lib/api";

const UnlockCtx = React.createContext({ open: () => {} });
export const useUnlock = () => React.useContext(UnlockCtx);

export default function Dashboard() {
  const { me, refresh } = useMe();
  const [modal, setModal] = useState({ open: false, tool: "" });

  // Dashboard is private + per-user; tell crawlers to skip it.
  React.useEffect(() => {
    let meta = document.head.querySelector('meta[name="robots"][data-dash]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      meta.setAttribute("data-dash", "");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "noindex, nofollow, noarchive");
    return () => {
      if (meta && meta.parentNode) meta.parentNode.removeChild(meta);
    };
  }, []);

  // Cross-device hydrate of Saved/Alerts/Applications/Playbooks lists.
  // Only fires once we know the user is logged in (skip when ?demo=… is on).
  React.useEffect(() => {
    if (!me?.ok || me?.demo) return;
    let cancel = false;
    (async () => {
      const ok = await hydrateFromServer();
      if (cancel || !ok) return;
      // hydrate has already broadcast jh:storage events; consumers self-refresh
    })();
    return () => {
      cancel = true;
    };
  }, [me?.ok, me?.demo]);

  const open = (tool = "") => setModal({ open: true, tool });
  const close = () => setModal({ open: false, tool: "" });

  return (
    <>
      <SEO
        title="Dashboard — JobHacki"
        description="Your JobHacki dashboard — live ATS jobs, resume match, readiness simulator, and every JobHacki tool in one place."
        canonical="https://jobhacki.com/dashboard"
      />
      <AuthGate me={me}>
        <Shell me={me} refresh={refresh}>
          <UnlockCtx.Provider value={{ open, paid: Boolean(me?.paid), me }}>
            <Outlet />
          </UnlockCtx.Provider>
        </Shell>
        <DashboardTour />
      </AuthGate>
      <UnlockModal open={modal.open} tool={modal.tool} onClose={close} />
      <DashboardViewBeacon />
      <LeadGateModal />
    </>
  );
}

/**
 * Fires `dashboard.view` once per dashboard session (per pathname change).
 * Drop-in beacon so we don't have to thread tracking into every nested page.
 */
function DashboardViewBeacon() {
  React.useEffect(() => {
    trackEvent("dashboard.view");
  }, []);
  return null;
}
